import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader, Loader, Badge, Empty } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { fmtPKR, fmtDate, statusColor } from "@/lib/format";

export default function Events() {
  const { t } = useI18n();
  const { user, setUser } = useAuth();
  const [events, setEvents] = useState(null);
  const [sel, setSel] = useState(null); // {event, team}
  const [amount, setAmount] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/events").then(({ data }) => setEvents(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!events) return <Loader />;

  const openConfirm = () => {
    if (!amount || Number(amount) <= 0) { toast.error("Enter a valid amount"); return; }
    if (Number(amount) < 2500) { toast.error("Minimum prediction amount is Rs 2,500"); return; }
    if (Number(amount) > (user?.balance || 0)) { toast.error(t("insufficient")); return; }
    setConfirming(true);
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post("/predictions", { event_id: sel.event.id, team: sel.team, amount: Number(amount) });
      toast.success("Prediction confirmed & saved to My Predictions!");
      const { data } = await api.get("/auth/me"); setUser(data);
      setSel(null); setConfirming(false); setAmount(""); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const canPredict = (ev) => ev.status === "upcoming" || ev.status === "open";

  return (
    <div>
      <PageHeader title={t("events")} subtitle="Predict the winner and stake your balance." />
      {events.length === 0 ? <Empty label={t("no_events")} /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((ev) => (
            <div key={ev.id} className="ec-card p-6" data-testid={`event-card-${ev.id}`}>
              <div className="flex items-center justify-between">
                <Badge className={statusColor(ev.status)}>{ev.status}</Badge>
                <span className="text-orange-400 font-semibold text-sm">{ev.reward_percentage}% {t("reward_percentage")}</span>
              </div>
              <h3 className="font-head text-lg font-semibold text-white mt-3">{ev.name}</h3>
              <p className="text-xs text-gray-500 mt-1">{fmtDate(ev.date + "T" + (ev.time || "00:00"))} · Closes {ev.closing_time}</p>
              <div className="grid grid-cols-2 gap-3 mt-5">
                {[ev.team_a, ev.team_b].map((team) => (
                  <button key={team} disabled={!canPredict(ev)} onClick={() => { setSel({ event: ev, team }); setAmount(""); }}
                    data-testid={`event-${ev.id}-team-${team}`}
                    className={`rounded-xl border p-3 text-sm font-semibold transition-all disabled:opacity-40 ${sel?.event.id === ev.id && sel?.team === team ? "border-orange-500 bg-orange-500/15 text-orange-400" : "border-white/10 bg-white/5 text-gray-200 hover:border-orange-500/40"}`}>
                    {team}
                  </button>
                ))}
              </div>
              {ev.final_result && <p className="text-xs mt-3" style={{}}>{ev.final_result === "Draw" ? <span className="text-amber-400">Result: Draw</span> : <span className="text-emerald-400">{t("winner")}: {ev.final_result}</span>}</p>}
              {ev.status === "postponed" && <p className="text-xs text-amber-400 mt-3" data-testid={`event-postponed-${ev.id}`}>⏳ Match Postponed{ev.postponed_until ? ` · ${ev.postponed_until}` : ""}</p>}
              {!canPredict(ev) && ev.status !== "postponed" && !ev.final_result && <p className="text-xs text-purple-400 mt-3">Predictions locked</p>}
            </div>
          ))}
        </div>
      )}

      {/* stake input dialog */}
      <Dialog open={!!sel && !confirming} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="ec-card border-white/10">
          <DialogHeader><DialogTitle className="font-head">{sel?.event.name}</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-4">
              <p className="text-sm text-gray-400">{t("selected_team")}: <span className="text-orange-400 font-semibold">{sel.team}</span></p>
              <div>
                <label className="text-sm text-gray-300">{t("amount")} (PKR)</label>
                <Input data-testid="stake-amount-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" placeholder="Minimum Rs 2,500" />
                <p className="text-xs text-gray-500 mt-1">{t("current_balance")}: {fmtPKR(user?.balance)}</p>
              </div>
              {amount > 0 && <p className="text-sm text-gray-300">Est. {t("reward_amount")}: <span className="text-emerald-400 font-semibold">{fmtPKR(Number(amount) * sel.event.reward_percentage / 100)}</span></p>}
              <Button onClick={openConfirm} data-testid="event-predict-stake-button" className="w-full bg-orange-600 hover:bg-orange-500 btn-glow font-semibold">{t("predict_now")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* confirmation dialog */}
      <Dialog open={confirming} onOpenChange={(o) => !o && setConfirming(false)}>
        <DialogContent className="ec-card border-white/10">
          <DialogHeader><DialogTitle className="font-head">{t("confirm_prediction")}</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-3 text-sm">
              {[[t("event"), sel.event.name], [t("selected_team"), sel.team], [t("amount"), fmtPKR(amount)],
                [t("date_time"), fmtDate(sel.event.date + "T" + (sel.event.time || "00:00"))],
                [t("reward_percentage"), sel.event.reward_percentage + "%"],
                [t("reward_amount"), fmtPKR(Number(amount) * sel.event.reward_percentage / 100)]].map(([l, v]) => (
                <div key={l} className="flex justify-between border-b border-white/5 pb-2"><span className="text-gray-400">{l}</span><span className="text-white font-medium">{v}</span></div>
              ))}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setConfirming(false)} className="flex-1 border-white/10 bg-white/5">{t("cancel")}</Button>
                <Button onClick={confirm} disabled={busy} data-testid="confirm-prediction-button" className="flex-1 bg-orange-600 hover:bg-orange-500 btn-glow font-semibold">{busy ? "..." : t("confirm")}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
